import { X } from "lucide-react";
import type { ClipboardEvent, CSSProperties, FocusEvent, FunctionComponent, KeyboardEvent, Ref } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";
import { controlBox, INNER_INPUT, useControlDefaults, type ControlSize, type ControlStatus } from "./control";

export interface TagsInputProps {
  value?: string[] | null;
  /** Receives the full list after each add/remove. */
  onChange?: (value: string[]) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /** Characters that also commit the typed text (Enter always does). Default `[","]`. */
  tokenSeparators?: string[];
  disabled?: boolean;
  size?: ControlSize;
  status?: ControlStatus;
  id?: string;
  name?: string;
  ref?: Ref<HTMLInputElement>;
  className?: string;
  style?: CSSProperties;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Free-form string list with removable chips (antd `Select mode="tags"` with the dropdown closed). */
export const TagsInput: FunctionComponent<TagsInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  tokenSeparators = [","],
  disabled,
  name,
  ref,
  className,
  style,
  ...rest
}) => {
  const { t } = useTranslation();
  const { size, status, id, aria } = useControlDefaults(rest);
  const [draft, setDraft] = useState("");
  const tags = value ?? [];

  const commit = (text: string) => {
    const splitter = new RegExp(tokenSeparators.map(escapeRegExp).join("|") || "$^");
    const added = text
      .split(splitter)
      .map((token) => token.trim())
      .filter((token, index, all) => token && !tags.includes(token) && all.indexOf(token) === index);
    setDraft("");
    if (added.length > 0) onChange?.([...tags, ...added]);
  };

  const remove = (index: number) => onChange?.(tags.filter((_, i) => i !== index));

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter/separators confirm an IME candidate (CJK, accents) — they must not commit a tag mid-composition.
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Enter" || tokenSeparators.includes(event.key)) {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && tags.length > 0) remove(tags.length - 1);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!tokenSeparators.some((separator) => text.includes(separator))) return;
    event.preventDefault();
    commit(draft + text);
  };

  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center gap-1 px-1 py-[3px] text-sm",
        controlBox(status, disabled),
        size === "small" ? "min-h-6 py-px" : size === "large" ? "min-h-10" : "min-h-8",
        className
      )}
      style={style}
    >
      {tags.map((tag, index) => (
        <span
          key={tag}
          className="inline-flex max-w-full items-center gap-1 rounded bg-noa-hover px-2 leading-6 text-noa"
        >
          <span className="truncate">{tag}</span>
          {!disabled && (
            <button
              type="button"
              aria-label={`${t("common:remove")} ${tag}`}
              // Keep focus in the input: its blur would commit the draft, and this click's `remove`
              // would then filter the stale list and drop the tag just added.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => remove(index)}
              className={cn(
                FOCUS_RING,
                "inline-flex cursor-pointer rounded-sm border-0 bg-transparent p-0 text-noa-muted hover:text-noa"
              )}
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      <input
        ref={ref}
        id={id}
        {...aria}
        name={name}
        value={draft}
        disabled={disabled}
        placeholder={tags.length === 0 ? placeholder : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={(event) => {
          commit(draft);
          onBlur?.(event);
        }}
        className={cn(INNER_INPUT, "h-6 min-w-16 px-1")}
      />
    </div>
  );
};
